import React, { createContext, useEffect, useState } from 'react';
import Themes from './Helpers/Themes';
import { Routes, Route, useNavigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import Toaster from './Components/Toaster';
import { AlertColor } from '@mui/material';
import { ApiConfig, getAxiosInstance, getConfig } from './ApiServices/Api';
import { ConfigOptions, IToaster } from './interfaces/ICommon';
import { PrivateRoute } from './Components/PrivateRoute';
import { PublicRoute } from './Components/PublicRoute';
import { SnackbarProvider } from 'notistack';
import routes from './Utils/routes';
import { Provider } from './Utils/firebaseconfig';
import { AuthContextProvider } from './Helpers/Contexts/authContext';
import { useAppContext } from './Helpers/Contexts/appContext';
import axios from 'axios';
import { KEY_AZURE_TOKEN } from './Helpers/constants';
import ApplicationFormNew from './Application/ApplicationForm/Application';
import 'react-datepicker/dist/react-datepicker.css';
import DatePicker from 'react-datepicker';
import 'react-date-picker/dist/DatePicker.css';
import 'react-calendar/dist/Calendar.css';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import AppLayout from './Components/AppLayout';

const Dashboard: React.LazyExoticComponent<React.FunctionComponent> = React.lazy(() => import('./Application/DashBoard/index'));
const AboutStartups: React.LazyExoticComponent<React.FunctionComponent> = React.lazy(() => import('./Application/AboutStartups/index'));
const InstructionForm: React.LazyExoticComponent<React.FunctionComponent> = React.lazy(() => import('./Application/StepperForm/InstructionForm/index'));
const Stepper: React.LazyExoticComponent<React.FunctionComponent> = React.lazy(() => import('./Application/StepperForm/index'));
const Loader: React.LazyExoticComponent<React.FunctionComponent> = React.lazy(() => import('./Components/Loaders/index'));
const LoginPage: React.LazyExoticComponent<React.FunctionComponent> = React.lazy(() => import('./Application/Auth/Login/index'));
const SignUpPage: React.LazyExoticComponent<React.FunctionComponent> = React.lazy(() => import('./Application/Auth/SignUp/index'));
const ForgotPassword: React.LazyExoticComponent<React.FunctionComponent> = React.lazy(() => import('./Application/Auth/ForgotPassword/index'));
const VerifyOTP: React.LazyExoticComponent<React.FunctionComponent> = React.lazy(() => import('./Application/Auth/VerifyOtp'));
const ConfirmPassword: React.LazyExoticComponent<React.FunctionComponent> = React.lazy(() => import('./Application/Auth/ForgotPassword/ConfirmPassword'));
const EmailConfirmation: React.LazyExoticComponent<React.FunctionComponent> = React.lazy(() => import('./Application/Auth/SignUp/ConfirmEmail/index'));
const PendingAction: React.LazyExoticComponent<React.FunctionComponent> = React.lazy(() => import('./Application/PendingAction/index'));
const FAQs: React.LazyExoticComponent<React.FunctionComponent> = React.lazy(() => import('./Application/FAQs/Questionnaire/index'));
const ViewAll: React.LazyExoticComponent<React.FunctionComponent> = React.lazy(() => import('./Application/ViewAll'));
const Forum: React.LazyExoticComponent<React.FunctionComponent> = React.lazy(() => import('./Application/Forums'));
const ForumDetails: React.LazyExoticComponent<React.FunctionComponent> = React.lazy(() => import('./Application/ForumDetails'));
const InstructionDetails: React.LazyExoticComponent<React.FunctionComponent> = React.lazy(() => import('./Components/Instructions'));
const CompleteFaq: React.LazyExoticComponent<React.FunctionComponent> = React.lazy(() => import('./Application/FAQs/CompleteFaq/index'));


const clientQuery = new QueryClient();

export const AppContext: React.Context<any> = createContext<any>({
  handleToaster(open: boolean, message: any, severity: any) { },
});

const MainLayout: React.FunctionComponent = () => {
  const {
    dispatch,
    state: { config },
  } = useAppContext();

  useEffect(() => {
    getConfig()
      .then((res: any) => {
        dispatch({ type: 'updateConfig', value: res?.config });
        dispatch({ type: 'updateLogo', value: res?.config?.logoURL });
        getAxiosInstance(res);
        try {
          axios({ method: 'get', baseURL: res?.config?.refreshTokenUrl })
            .then((response: any) => {
              sessionStorage.setItem(KEY_AZURE_TOKEN, response.data.access_token);
            })
            .catch((err) => {
              return err;
            });
        } catch (err) {
          console.error(err);
        }
      })
      .catch((err) => {
        console.log('error in config', err);
      });
  }, []);

  const [openToaster, setOpenToaster] = useState<IToaster>({
    message: '',
    open: false,
    severity: 'info',
  });

  const handleToaster: (open: boolean, message: string, severity: AlertColor) => void = (open: boolean, message: string, severity: AlertColor) => {
    setOpenToaster({
      message: message,
      open: open,
      severity: severity,
    });
  };

  const contextData: any = {
    handleToaster: handleToaster,
  };

  // Define the time duration in milliseconds for 6 hours
  const sixHours = 6 * 60 * 60 * 1000;

  const navigate = useNavigate();
  setTimeout(() => {
    sessionStorage.clear();
    navigate(routes.login);
  }, sixHours);

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <QueryClientProvider client={clientQuery}>
        <AppContext.Provider value={contextData}>
          <AuthContextProvider>
            <SnackbarProvider autoHideDuration={5000}>
              <Toaster
                open={openToaster.open}
                handleSnackClose={() => {
                  setOpenToaster({
                    message: '',
                    open: false,
                    severity: 'info',
                  });
                }}
                message={openToaster.message}
                status={openToaster.severity}
              />
              <Themes>
                <React.Suspense fallback={<Loader />}>
                  <Routes>
                    <Route
                      path={routes.login}
                      index
                      element={
                        <PublicRoute>
                          <LoginPage />
                        </PublicRoute>
                      }
                    />
                    <Route
                      path={routes.signup}
                      element={
                        <PublicRoute>
                          <SignUpPage />
                        </PublicRoute>
                      }
                    />
                    <Route
                      path={routes.emailverification}
                      element={
                        <PublicRoute>
                          <EmailConfirmation />
                        </PublicRoute>
                      }
                    />
                    <Route
                      path={routes.forgotPassword}
                      element={
                        <PublicRoute>
                          <ForgotPassword />
                        </PublicRoute>
                      }
                    />
                    <Route
                      path={routes.otpverification}
                      element={
                        <PublicRoute>
                          <VerifyOTP />
                        </PublicRoute>
                      }
                    />
                    <Route
                      path={routes.changePassword}
                      element={
                        <PublicRoute>
                          <ConfirmPassword />
                        </PublicRoute>
                      }
                    />
                    <Route
                      path={routes.dashboard}
                      element={
                        <PrivateRoute>
                          <Dashboard />
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path={routes.viewAll}
                      element={
                        <PrivateRoute>
                          <ViewAll />
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path={routes.forum}
                      element={
                        <PrivateRoute>
                          <Forum />
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path={routes.forumDetails}
                      element={
                        <PrivateRoute>
                          <ForumDetails />
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path={routes.instruction}
                      element={
                        <PrivateRoute>
                          <AppLayout>
                            <InstructionDetails />
                          </AppLayout>
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="/events/:id"
                      element={
                        <PrivateRoute>
                          <AboutStartups />
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="/application/:eventId?/:formId?/:step?"
                      element={
                        <PrivateRoute>
                          <Stepper />
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="/form"
                      element={
                        <PrivateRoute>
                          <ApplicationFormNew />
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="/apply/instruction/:eventId"
                      element={
                        <PrivateRoute>
                          <InstructionForm />
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path={routes.pendingAction}
                      element={
                        <PrivateRoute>
                          <PendingAction />
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path={routes.faqs}
                      element={
                        <PrivateRoute>
                          <FAQs />
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path={routes.completefaq}
                      element={
                        <PrivateRoute>
                          <AppLayout>
                            <CompleteFaq />
                          </AppLayout>
                        </PrivateRoute>
                      }
                    />
                  </Routes>
                </React.Suspense>
              </Themes>
            </SnackbarProvider>
          </AuthContextProvider>
        </AppContext.Provider>
      </QueryClientProvider>
    </LocalizationProvider>
  );
};

const App: React.FunctionComponent = () => {
  ApiConfig();
  return <MainLayout />;
};

export default App;
